import type { HeroProps } from '@/components/home/Hero';
import type {
  CustomersReviewsProps,
  GenericProductsProps,
  HowItWorksProps,
  InstantEverythingProps,
  PricesProps,
} from '@/components/shared';
import type { TrustProps } from '@/components/shared/Trust';
import type { BasePageConfig } from '@/pageConfig/BasePageConfig';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import room from '@/static/assets/home/living-room.svg';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_de-DE.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_de.svg';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  genericProducts: GenericProductsProps;
  customersReviews: CustomersReviewsProps;
  instantEverything: InstantEverythingProps;
  prices: PricesProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
}

export const config: Config = {
  seo: { ...sharedConfig['de-DE'].seo },
  layout: {
    ...sharedConfig['de-DE'].layout,
    showAskUsAnything: true,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['de-DE'].layout.headerActions,
      CTAs: {
        ...sharedConfig['de-DE'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['de-DE'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check unsere preise',
            plural: 'Check unsere preise',
          },
        },
      },
    },
  },
  hero: {
    title: 'Vergiss alles, was du über\nVersicherungen weißt',
    subtitle: 'Alles im Nu. Dufte Preise. Großes Herz.',
    ctaLabel: 'Check unsere preise',
    cart: {
      singular: 'Angebot',
      plural: 'Zurück zu deinem Angebot',
    },
    includePressBanner: 'withLogos',
  },
  genericProducts: {
    title: 'Hausrat und Privathaftpflicht für<br/>Andersdenkende',
    subtitle: 'Lemonades Versicherung schützt dich und deine Sachen',
    bgImage: room.src,
  },
  instantEverything: {
    title: 'Alles im Nu',
    subtitle:
      'Maya, unser charmanter Bot, wird mit Hilfe von Künstlicher Intelligenz eine\nindividuelle Versicherung für dich gestalten. Es könnte nicht einfacher sein, oder schneller',
    appSketchContentLeft: 'In <b>Sekunden</b>\nversichert',
    appSketchContentRight: 'In <b>Minuten</b>\nausbezahlt',
    darkBackground: true,
    adjustments: {
      contentSectionLeft: {
        left: { lg: '-435px' },
      },
      contentSectionRight: {
        right: { lg: '-415px' },
      },
    },
    layout: {
      mt: [-30, -50],
    },
  },
  prices: {
    title: 'Monatliche Laufzeit\nJederzeit kündbar',
    cardsData: [
      {
        title: 'Nur\nHausrat',
        subtitle: 'MONATLICH AB*',
        price: '2€',
        cta: {
          ctaLabel: 'HAUSRAT ABSCHLIESSEN',
          ctaUrl: '/onboarding/start?p=home_eu_contents',
        },
      },
      {
        title: 'Nur\nHaftpflicht',
        subtitle: 'MONATLICH AB*',
        price: '2€',
        cta: {
          ctaLabel: 'HAFTPFLICHT ABSCHLIESSEN',
          ctaUrl: '/onboarding/start?p=home_eu_liability',
        },
      },
      {
        title: 'Hausrat und\nHaftpflicht',
        subtitle: 'MONATLICH AB*',
        price: '4€',
        cta: {
          ctaLabel: 'KOMBI ABSCHLIESSEN!',
          ctaUrl: '/onboarding/start?p=home_eu_contents_and_liability',
        },
      },
    ],
  },
  customersReviews: {
    title: 'Was Leute über uns sagen',
    showStars: false,
    darkBackground: true,
    shortCard: false,
  },
  howItWorks: {
    title: 'Wie Lemonade funktioniert',
    showBrandsImage: false,
    subtitle:
      'Lemonade stellt das traditionelle Versicherungsmodell auf den Kopf. Wir behandeln die Beiträge, die du zahlst, als ob es dein Geld ist – nicht unseres. Mit Lemonade wird alles einfach und transparent: Wir nehmen einen fixen Anteil, bezahlen Schadensfälle super schnell und spenden den Restbetrag an einen wohltätigen Zweck deiner Wahl!*',
    disclaimer: `* Vorbehaltlich des Ermessens des Vorstands und der Einhaltung bestimmter finanzieller Standards durch das Unternehmen. <a href="/de/faq#giveback">Lies mehr dazu hier</a>`,
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: 'Ein <b>fixer Anteil</b>, um </br> alles am Laufen zu halten',
      },
      {
        image: pizza.mobile[2].src,
        text: 'Wir bezahlen Ansprüche<br/><b>super schnell</b>',
      },
      {
        image: pizza.mobile[3].src,
        text: 'Mit Erfolg und Haltung <b>gemeinsam Gutes tun</b>',
      },
    ],
    layout: {
      mt: [-30, -50],
    },
  },
  trust: {
    bulletsLayout: 'vertical',
    darkBackground: true,
    showTopBorder: true,
    bullets: [
      {
        name: 'social',
        title: 'Konzipiert für soziales Engagement',
        text: 'Lemonade Inc. ist eine Public Benefit Corporation und B-Corp zertifiziert. Soziales Engagement ist Teil unserer rechtlichen Vorgaben sowie unseres Geschäftsmodells - und kein leeres Versprechen',
        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'rating',
        title: 'A-Rated & abgesichert durch Marktführer',
        text: 'Lemonade Inc. ist mit einem A-Rating eingestuft, vollständig lizenziert und durch die vertrauenswürdigsten Big Players rückversichert',
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
};
